/**
 * `external-sharing` Phase 6 Session 6.1 — `useVersionGraph(shareId)`.
 *
 * TanStack Query wrapper over `GET /api/shares/:shareId/version-graph` (Phase 2
 * Session 2.6). Returns the full fork DAG rooted at the conversation associated
 * with the input share. Server gates access (owner OR recipient of any node in
 * the chain); 404 indicates the share doesn't exist OR the caller has no
 * connection to its chain.
 *
 * The response is conversation-rooted (not share-rooted) per
 * `packages/shared-types/src/lib/sharing.ts:130-134` — each node is a
 * conversation; parent-child edges traverse via `forkedFromShareId` matching
 * a sibling node's `childShares[].shareId`.
 *
 * Cache key intentionally keyed by SHARE id (not conversation id) — two
 * different shares of the same conversation get separate cache entries because
 * the auth-scoped graph projection can differ (one caller might be the owner
 * of share A but only a recipient of share B; same root conversation, same
 * underlying topology, but the server's access projection is per-share).
 */
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import { getVersionGraph } from '@/lib/shares-api';
import { queryKeys } from '@/lib/query-keys';
import type { VersionGraphResponse } from '@arcanum-hq/shared-types';

export function useVersionGraph(shareId: string | null | undefined) {
  return useQuery<VersionGraphResponse>({
    queryKey: queryKeys.shares.versionGraph(shareId ?? ''),
    queryFn: () => {
      if (!shareId) throw new Error('shareId required');
      return getVersionGraph(shareId);
    },
    enabled: !!shareId,
    staleTime: 30_000,
    // 404 is a stable terminal state for the lifetime of the lookup — don't
    // burn retries on it.
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
  });
}
